/**
 * OpExTable - Operating Expenditure Configuration
 *
 * Allows users to configure annual O&M costs and fuel prices.
 * Follows same pattern as BudgetTab (CapEx).
 */

import React from 'react';
import {
    Box, Card, CardContent, Typography, Table, TableHead, TableBody,
    TableRow, TableCell, TextField, Alert
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { formatCurrency, formatNumber } from '@vortx/shared-ui';

// Format input number with commas
function formatInputNumber(value) {
    if (!value || value === '') return '';
    const num = parseFloat(value.toString().replace(/,/g, ''));
    if (isNaN(num)) return '';
    return num.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

/**
 * OpExTable Props:
 * @param {Object} systemSizes - { pvKw, batteryKw, batteryKwh, generatorKw }
 * @param {Object} budget - Budget state from useBudget hook
 * @param {boolean} hasSystemConfigured - Whether any DER is configured
 * @param {boolean} readOnly - Whether to show in read-only mode
 * @param {Object} fuelCostBreakdown - Optional fuel cost breakdown { deliveryCost, commodityCost, totalCost }
 */
function OpExTable({ systemSizes = {}, budget, hasSystemConfigured = false, readOnly = false, fuelCostBreakdown = null }) {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const {
        opexInputs,
        opexCalculations,
        updateOpexInput,
        loading
    } = budget;

    const { pvKw = 0, batteryKw = 0, batteryKwh = 0, generatorKw = 0 } = systemSizes;

    // Show empty state if no system configured
    if (!hasSystemConfigured) {
        return (
            <Card>
                <CardContent>
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 600, mb: 2 }}>
                            Configure Your Microgrid First
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Go to the <strong>Microgrid Design</strong> tab to configure your DER system (PV, Battery, Generator),
                            then return here to set up operating costs.
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        );
    }

    if (loading) {
        return (
            <Card>
                <CardContent>
                    <Typography>Loading defaults...</Typography>
                </CardContent>
            </Card>
        );
    }

    return (
        <Box>
            {/* Header */}
            {!readOnly && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Operating Expenditure (OpEx)
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Configure annual operations & maintenance costs. O&M costs escalate using the Labor rate from Annual Escalation Rates.
                        </Typography>
                    </CardContent>
                </Card>
            )}

            {/* Info message for readOnly mode */}
            {readOnly && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    This is a read-only view of your OpEx. To edit values, go to the <strong>Budget</strong> tab.
                </Alert>
            )}

            {/* OpEx Table */}
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>Annual O&M Cost Breakdown (Year 1)</Typography>
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: 800 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ minWidth: 180 }}>Component</TableCell>
                                    <TableCell align="center" sx={{ minWidth: 120 }}>Size</TableCell>
                                    <TableCell align="center" sx={{ minWidth: 150 }}>Rate</TableCell>
                                    <TableCell align="right" sx={{ minWidth: 120, fontWeight: 600 }}>Annual Cost</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {/* Equipment O&M */}
                                <TableRow sx={{ backgroundColor: isDark ? 'rgba(100, 150, 200, 0.1)' : '#f0f8ff' }}>
                                    <TableCell colSpan={4}><strong>EQUIPMENT O&M</strong></TableCell>
                                </TableRow>

                                {/* PV O&M */}
                                {pvKw > 0 && (
                                    <TableRow>
                                        <TableCell sx={{ pl: 3 }}>Solar PV O&M</TableCell>
                                        <TableCell align="center">{formatNumber(pvKw)} kW</TableCell>
                                        <TableCell align="center">
                                            {readOnly ? (
                                                <Typography variant="body2">
                                                    ${formatNumber(opexInputs.pv?.rate || 0, 2)}/kW/year
                                                </Typography>
                                            ) : (
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                    <TextField
                                                        size="small"
                                                        value={formatInputNumber(opexInputs.pv?.rate)}
                                                        onChange={(e) => updateOpexInput('pv', 'rate', e.target.value)}
                                                        sx={{ width: 80 }}
                                                        InputProps={{ startAdornment: '$' }}
                                                    />
                                                    <Typography variant="body2">/kW/year</Typography>
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <strong>{formatCurrency(opexCalculations?.pvOM || 0)}</strong>
                                        </TableCell>
                                    </TableRow>
                                )}

                                {/* Battery O&M */}
                                {batteryKwh > 0 && (
                                    <TableRow>
                                        <TableCell sx={{ pl: 3 }}>Battery O&M</TableCell>
                                        <TableCell align="center">{formatNumber(batteryKwh)} kWh</TableCell>
                                        <TableCell align="center">
                                            {readOnly ? (
                                                <Typography variant="body2">
                                                    ${formatNumber(opexInputs.battery?.rate || 0, 2)}/kWh/year
                                                </Typography>
                                            ) : (
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                    <TextField
                                                        size="small"
                                                        value={formatInputNumber(opexInputs.battery?.rate)}
                                                        onChange={(e) => updateOpexInput('battery', 'rate', e.target.value)}
                                                        sx={{ width: 80 }}
                                                        InputProps={{ startAdornment: '$' }}
                                                    />
                                                    <Typography variant="body2">/kWh/year</Typography>
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <strong>{formatCurrency(opexCalculations?.batteryOM || 0)}</strong>
                                        </TableCell>
                                    </TableRow>
                                )}

                                {/* Generator O&M */}
                                {generatorKw > 0 && (
                                    <TableRow>
                                        <TableCell sx={{ pl: 3 }}>Generator O&M</TableCell>
                                        <TableCell align="center">{formatNumber(generatorKw)} kW</TableCell>
                                        <TableCell align="center">
                                            {readOnly ? (
                                                <Typography variant="body2">
                                                    ${formatNumber(opexInputs.generator?.rate || 0, 2)}/kW/year
                                                </Typography>
                                            ) : (
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                    <TextField
                                                        size="small"
                                                        value={formatInputNumber(opexInputs.generator?.rate)}
                                                        onChange={(e) => updateOpexInput('generator', 'rate', e.target.value)}
                                                        sx={{ width: 80 }}
                                                        InputProps={{ startAdornment: '$' }}
                                                    />
                                                    <Typography variant="body2">/kW/year</Typography>
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <strong>{formatCurrency(opexCalculations?.generatorOM || 0)}</strong>
                                        </TableCell>
                                    </TableRow>
                                )}

                                {/* Fixed Annual O&M */}
                                <TableRow>
                                    <TableCell sx={{ pl: 3 }}>Fixed Annual O&M</TableCell>
                                    <TableCell align="center">-</TableCell>
                                    <TableCell align="center">
                                        {readOnly ? (
                                            <Typography variant="body2">
                                                {formatCurrency(opexInputs.fixedAnnual?.rate || 0)}/year
                                            </Typography>
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                <TextField
                                                    size="small"
                                                    value={formatInputNumber(opexInputs.fixedAnnual?.rate)}
                                                    onChange={(e) => updateOpexInput('fixedAnnual', 'rate', e.target.value)}
                                                    sx={{ width: 100 }}
                                                    InputProps={{ startAdornment: '$' }}
                                                />
                                                <Typography variant="body2">/year</Typography>
                                            </Box>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <strong>{formatCurrency(opexCalculations?.fixedAnnualOM || 0)}</strong>
                                    </TableCell>
                                </TableRow>

                                {/* Custom O&M */}
                                <TableRow>
                                    <TableCell sx={{ pl: 3 }}>
                                        {readOnly ? (
                                            <Typography variant="body2">
                                                {opexInputs.custom?.label || 'Custom O&M'}
                                            </Typography>
                                        ) : (
                                            <TextField
                                                size="small"
                                                variant="standard"
                                                placeholder="Custom O&M"
                                                value={opexInputs.custom?.label || ''}
                                                onChange={(e) => updateOpexInput('custom', 'label', e.target.value)}
                                                sx={{ width: 150 }}
                                                InputProps={{ sx: { fontSize: '0.875rem' } }}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell align="center">-</TableCell>
                                    <TableCell align="center">
                                        {readOnly ? (
                                            <Typography variant="body2">
                                                {formatCurrency(opexInputs.custom?.rate || 0)}/year
                                            </Typography>
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                                <TextField
                                                    size="small"
                                                    value={formatInputNumber(opexInputs.custom?.rate)}
                                                    onChange={(e) => updateOpexInput('custom', 'rate', e.target.value)}
                                                    sx={{ width: 100 }}
                                                    InputProps={{ startAdornment: '$' }}
                                                />
                                                <Typography variant="body2">/year</Typography>
                                            </Box>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <strong>{formatCurrency(opexCalculations?.customOM || 0)}</strong>
                                    </TableCell>
                                </TableRow>

                                {/* Total O&M */}
                                <TableRow sx={{ backgroundColor: isDark ? 'rgba(63, 106, 100, 0.2)' : 'rgba(63, 106, 100, 0.1)' }}>
                                    <TableCell colSpan={3}><strong>TOTAL ANNUAL O&M (Year 1)</strong></TableCell>
                                    <TableCell align="right">
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                            {formatCurrency(opexCalculations?.totalAnnualOM || 0)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>

                                {/* Fuel Costs Section - shown when generator exists and fuel cost data is available */}
                                {generatorKw > 0 && fuelCostBreakdown && (
                                    <>
                                        <TableRow sx={{ backgroundColor: isDark ? 'rgba(100, 150, 200, 0.1)' : '#f0f8ff' }}>
                                            <TableCell colSpan={4}><strong>FUEL COSTS (Year 1)</strong></TableCell>
                                        </TableRow>
                                        {fuelCostBreakdown.deliveryCost !== undefined && fuelCostBreakdown.deliveryCost !== null && (
                                            <TableRow>
                                                <TableCell sx={{ pl: 3 }}>Fuel - Delivery</TableCell>
                                                <TableCell align="center">-</TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2">Gas tariff delivery rate</Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <strong>{formatCurrency(fuelCostBreakdown.deliveryCost)}</strong>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {fuelCostBreakdown.commodityCost !== undefined && fuelCostBreakdown.commodityCost !== null && (
                                            <TableRow>
                                                <TableCell sx={{ pl: 3 }}>Fuel - Commodity</TableCell>
                                                <TableCell align="center">-</TableCell>
                                                <TableCell align="center">
                                                    <Typography variant="body2">Gas tariff commodity rate</Typography>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <strong>{formatCurrency(fuelCostBreakdown.commodityCost)}</strong>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        <TableRow sx={{ backgroundColor: isDark ? 'rgba(255, 179, 71, 0.2)' : 'rgba(255, 179, 71, 0.1)' }}>
                                            <TableCell colSpan={3}><strong>TOTAL ANNUAL FUEL COST (Year 1)</strong></TableCell>
                                            <TableCell align="right">
                                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#ff9800' }}>
                                                    {formatCurrency(fuelCostBreakdown.totalCost || 0)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    </>
                                )}

                            </TableBody>
                        </Table>
                    </Box>

                    {/* Additional Info */}
                    <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            <strong>Note:</strong> O&M costs escalate using the Labor escalation rate configured in Annual Escalation Rates.
                            {generatorKw > 0 && !fuelCostBreakdown && ' Fuel costs for generator operation are calculated separately based on actual generation and efficiency.'}
                            {generatorKw > 0 && fuelCostBreakdown && ' Fuel costs shown above are based on gas tariff with separate delivery and commodity rates.'}
                        </Typography>
                    </Alert>
                </CardContent>
            </Card>
        </Box>
    );
}

export default OpExTable;
